// 2026-11-03 효과음 변형(SeedAudio 1.0, tools/audio/seed-sfx-manifest.json). 같은 이름의 소리를 여러 파일로 두고 매번 다르게 고른다(반복 피로 방지).
// 파일: 0번 = public/assets/audio/sfx/<이름>.mp3, n번 = <이름>-v<n>.mp3. 표에 없는 이름은 파일 1개.
import type { SfxName } from './sounds';

/** 이름 → 변형 개수(0번 포함). 시험이 manifest·파일과 맞는지 확인한다 */
export const SFX_VARIANTS: Readonly<Partial<Record<SfxName, number>>> = {
  swing0: 4,
  swing1: 4,
  swing2: 4,
  swing3: 4,
  swingSkill: 3,
  hit0: 3,
  hit1: 3,
  hit2: 3,
  hitCrit: 4,
  hitFinisher: 3,
  hitSkill: 3,
  hitLight: 3,
  hitBoss: 3,
  blocked: 3,
  heroHurt: 4,
  dodge: 4,
  kill: 4,
  killBig: 3,
  mobAttack: 4,
  mobSpawn: 3,
  skill1: 3,
  skill2: 3,
  skill3: 3,
  skill3Land: 3,
  skill4Cast: 3,
  skill4Wave: 3,
  bossIntro: 3,
  bossDefeated: 3,
  emberIntro: 3,
  uiClick: 3,
  uiConfirm: 3,
  cardPick: 3,
  levelUp: 3,
  drop0: 3,
  drop1: 3,
  drop2: 3,
  drop3: 3,
  drop4: 3,
  pickupItem: 4,
  potionDrink: 3,
  magicItem: 3,
  uiDeny: 3,
  equipGear: 4,
  buyCoins: 3,
  heroSelect: 3,
  koImpact: 3,
};

export function variantCount(name: SfxName): number {
  return Math.max(1, SFX_VARIANTS[name] ?? 1);
}

export function variantFile(name: string, v: number): string {
  return v === 0 ? name + '.mp3' : name + '-v' + v + '.mp3';
}

/** 직전 변형을 피해 하나 고른다(r ∈ [0,1)). 할당 없음 */
export function pickVariant(count: number, last: number, r: number): number {
  if (count <= 1) return 0;
  if (last < 0 || last >= count) return Math.min(count - 1, Math.floor(r * count));
  const i = Math.min(count - 2, Math.floor(r * (count - 1)));
  return i >= last ? i + 1 : i;
}

/**
 * 내려받은 변형 중에서만 고른다(없는 변형 자리는 건너뜀). 하나도 없으면 -1(무음).
 * 0번 파일이 실패해도 다른 변형이 있으면 재생한다. last는 직전에 고른 변형 번호. 할당 없음.
 */
export function pickLoadedVariant(bufs: ArrayLike<unknown>, last: number, r: number): number {
  let n = 0, lastRank = -1;
  for (let i = 0; i < bufs.length; i++) {
    if (!bufs[i]) continue;
    if (i === last) lastRank = n;
    n++;
  }
  if (n === 0) return -1;
  let rank = pickVariant(n, lastRank, r);
  for (let i = 0; i < bufs.length; i++) {
    if (!bufs[i]) continue;
    if (rank === 0) return i;
    rank--;
  }
  return -1;
}

/** 같은 소리가 동시에 이만큼 울리고 있으면 그중 가장 오래된 것을 끊고 쓴다(겹침으로 생기는 위상 뭉개짐·음량 폭주 방지) */
export const MAX_SAME_SFX = 3;
/** 피치 흔들림이 켜진 소리의 음량 미세 변화(±dB) */
export const GAIN_JITTER_DB = 0.75;
